
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Helmet } from "react-helmet";
import { getAllPosts, getPostData } from "../../utils/markdownLoader";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import BlogBottomCTA from "../../components/blogSection/bottomCTA";
import "../../assets/css/Hero.css";
import TwitterIcon from '@mui/icons-material/Twitter';
import XIcon from '@mui/icons-material/X';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';

// ... (helper functions)

const slugify = (text) => {
    return text.toString().toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
};

const BlogPost = () => {
    const { slug } = useParams();
    const [postData, setPostData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [headings, setHeadings] = useState([]);
    const [activeId, setActiveId] = useState("");
    const [recommendedPosts, setRecommendedPosts] = useState([]);
    const [trendingPosts, setTrendingPosts] = useState([]);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        const loadPost = async () => {
            try {
                const allPostPaths = await getAllPosts();
                const matchingPost = allPostPaths.find(p => p.slug === slug);

                if (matchingPost) {
                    const data = await getPostData(matchingPost.path);
                    setPostData(data);

                    // Fetch all other posts to determine Trending vs Recommended
                    const allPostsData = await Promise.all(
                        allPostPaths
                            .filter(p => p.slug !== slug)
                            .map(async (p) => {
                                const pData = await getPostData(p.path);
                                return { ...pData, slug: p.slug };
                            })
                    );

                    // Sort by date for Trending
                    allPostsData.sort((a, b) => new Date(b.date) - new Date(a.date));

                    // Trending = Top 3 recent (excluding current)
                    setTrendingPosts(allPostsData.slice(0, 3));

                    // Recommended = Random 4 or Next 4
                    setRecommendedPosts(allPostsData.slice(0, 4));

                    // Extract headings for ToC
                    const lines = data.content.split('\n');
                    const extractedHeadings = lines.filter(line => line.match(/^##/))
                        .map(line => {
                            const level = line.match(/^#+/)[0].length;
                            const text = line.replace(/^#+\s+/, '').trim();
                            return { id: slugify(text), text, level };
                        });
                    setHeadings(extractedHeadings);
                }
            } catch (err) {
                console.error("Failed to load post", err);
            } finally {
                setLoading(false);
            }
        };
        loadPost();
    }, [slug]);

    // ScrollSpy to highlight active heading
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id);
                    }
                });
            },
            { rootMargin: "-100px 0px -60% 0px" }
        );

        headings.forEach((heading) => {
            const element = document.getElementById(heading.id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, [headings]);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (loading) return <div>Loading...</div>;
    if (!postData) return <div style={{ textAlign: "center", marginTop: "100px" }}>Post not found</div>;

    // Calculate read time
    const calculateReadTime = (content) => {
        const wordsPerMinute = 200;
        const words = content.trim().split(/\s+/).length;
        const time = Math.ceil(words / wordsPerMinute);
        return `${time} min read`;
    };

    const readTime = postData.content ? calculateReadTime(postData.content) : "5 min read";
    const shareUrl = window.location.href;
    const shareTitle = postData.title;

    return (
        <div>
            <Helmet>
                <title>{postData.title} | Comparedly Blog</title>
                <meta name="description" content={postData.description || "Read more about this topic on Comparedly."} />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href={`https://comparedly.com/blog/${slug}`} />
                <meta property="og:title" content={postData.title} />
                <meta property="og:description" content={postData.description} />
                <meta property="og:image" content={postData.image ? (postData.image.startsWith('http') ? postData.image : `https://comparedly.com${postData.image}`) : ""} />
                <meta property="og:url" content={`https://comparedly.com/blog/${slug}`} />
                <meta property="twitter:card" content="summary_large_image" />
            </Helmet>
            <Navbar />
            <div className="blog-post-wrapper">
                {/* Back Link */}
                <div className="back-link-container" style={{ maxWidth: '1200px', margin: '20px auto 0', padding: '0 20px' }}>
                    <Link to="/blog" style={{ textDecoration: 'none', color: '#555', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>&larr;</span> Back to Blog
                    </Link>
                </div>

                {/* Header Section */}
                <header className="blog-header">
                    <h1 className="blog-full-title">{postData.title}</h1>
                    <div className="blog-full-meta">
                        <span>{postData.date}</span>
                        <span>•</span>
                        <span>{readTime}</span> {/* Dynamic Read Time */}
                        <span>•</span>
                        <span>{postData.author}</span>
                    </div>

                    {/* Social Share Buttons */}
                    <div className="blog-share-container">
                        <span className="share-label">Share:</span>
                        <a
                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="share-btn x-twitter"
                        >
                            <XIcon fontSize="small" />
                        </a>
                        <a
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="share-btn linkedin"
                        >
                            <LinkedInIcon fontSize="small" />
                        </a>
                        <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="share-btn facebook"
                        >
                            <FacebookIcon fontSize="small" />
                        </a>
                        <button className="share-btn copy-link" onClick={handleCopyLink} title="Copy Link">
                            {copied ? <CheckIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
                        </button>
                    </div>
                </header>

                {/* Hero Image */}
                {postData.image && (
                    <img
                        src={postData.image}
                        alt={postData.title}
                        className="blog-hero-image"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                )}

                {/* 3-Column Layout: Left ToC | Content | Right Widgets */}
                <div className="blog-layout">

                    {/* LEFT SIDEBAR: Table of Contents */}
                    <aside className="blog-sidebar-left">
                        <div className="toc-container">
                            <h4 className="toc-title">Table of Contents</h4>
                            <ul className="toc-list">
                                {headings.map(heading => (
                                    <li key={heading.id} className={`toc-item level-${heading.level} ${activeId === heading.id ? 'active' : ''}`}>
                                        <a
                                            href={`#${heading.id}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                document.getElementById(heading.id)?.scrollIntoView({ behavior: "smooth" });
                                                setActiveId(heading.id);
                                            }}
                                        >
                                            {heading.text}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>

                    {/* MAIN CONTENT */}
                    <article className="blog-main-content">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                            components={{
                                h1: ({ node, ...props }) => <h1 id={slugify(props.children)} {...props} />,
                                h2: ({ node, ...props }) => <h2 id={slugify(props.children)} {...props} />,
                                h3: ({ node, ...props }) => <h3 id={slugify(props.children)} {...props} />,
                                p: ({ node, ...props }) => <p {...props} />,
                                li: ({ node, ...props }) => <li {...props} />,
                                img: ({ node, ...props }) => <img style={{ maxWidth: "100%", borderRadius: "8px" }} {...props} />,
                                a: ({ node, ...props }) => {
                                    if (props.href && props.href.startsWith('/auth')) {
                                        return (
                                            <a
                                                {...props}
                                                href={`${process.env.REACT_APP_URL}${props.href}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    window.location.href = `${process.env.REACT_APP_URL}${props.href}`;
                                                }}
                                            />
                                        );
                                    }
                                    return <a {...props} />;
                                }
                            }}
                        >
                            {postData.content}
                        </ReactMarkdown>
                    </article>

                    {/* RIGHT SIDEBAR: Widgets */}
                    <aside className="blog-sidebar-right">
                        <div className="sidebar-widget cta-widget">
                            <h3 className="cta-title">Try Comparedly free with a 14-day trial</h3>
                            <p className="cta-desc">
                                Utilise Comparedly's robust analytics to benchmark, track, and optimize your social media strategy.
                            </p>
                            <button
                                className="btn-primary full-width cta-btn"
                                onClick={() => window.location.href = `${process.env.REACT_APP_URL}/auth/register`}
                            >
                                Start Your Free Trial
                            </button>
                            <p className="cta-note">* No Credit Card Required</p>
                        </div>

                        <div className="sidebar-widget">
                            <h3 className="widget-title">Trending</h3>
                            <ul className="sidebar-list">
                                {trendingPosts.map(post => (
                                    <li key={post.slug}>
                                        <Link to={`/blog/${post.slug}`}>
                                            {post.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>
                </div>

                {/* Recommended Section */}
                {recommendedPosts.length > 0 && (
                    <div className="recommended-section">
                        <div className="recommended-header">
                            <h3>Recommended for you</h3>
                            <a href="/blog" className="view-all-link">View all &gt;</a>
                        </div>
                        <div className="recommended-grid">
                            {recommendedPosts.map(post => (
                                <Link
                                    to={`/blog/${post.slug}`}
                                    key={post.slug}
                                    className="recommended-card"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    {post.image && (
                                        <img src={post.image} alt={post.title} className="rec-card-image" />
                                    )}
                                    <div className="rec-card-content">
                                        <div className="rec-meta">
                                            <span className="rec-tag">{post.categories?.[0] || "Blog"}</span>
                                        </div>
                                        <h4 className="rec-title">
                                            {post.title}
                                        </h4>
                                        <div className="rec-date">{post.date} • {post.readTime || "5 min read"}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <BlogBottomCTA />
            <Footer />
        </div>
    );
};

export default BlogPost;
